import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../i18n/LanguageContext";
import type { Language } from "../i18n/translations";
import { downloadDiaryCsv } from "../api/diaryApi";

interface AppHeaderProps {
  darkMode: boolean;
  onToggleDark: () => void;
}

function getUsernameFromToken(): string {
  try {
    const token = localStorage.getItem("token");
    if (!token) return "";
    const payload = JSON.parse(atob(token.split(".")[1]));
    return (payload.sub as string) ?? "";
  } catch {
    return "";
  }
}

export function AppHeader({ darkMode, onToggleDark }: AppHeaderProps) {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const username = getUsernameFromToken();
  const { language, setLanguage, t } = useLanguage();

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close settings modal on Escape
  useEffect(() => {
    if (!settingsOpen) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") setSettingsOpen(false);
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [settingsOpen]);

  function handleLogout() {
    localStorage.removeItem("token");
    navigate("/login");
  }

  function openSettings() {
    setMenuOpen(false);
    setSettingsOpen(true);
  }

  async function handleExportCsv() {
    try {
      setExporting(true);
      await downloadDiaryCsv();
    } catch {
      // silently ignore — download errors are rare and visible via browser
    } finally {
      setExporting(false);
    }
  }

  return (
    <>
      <header className="app-header">
        <div className="app-header-brand">
          <svg
            className="app-header-logo"
            width="40"
            height="40"
            viewBox="0 0 32 32"
            fill="none"
            aria-hidden="true"
          >
            <circle cx="16" cy="16" r="16" fill="rgba(255,255,255,0.15)" />
            <path
              d="M16 7C16 7 8 11 8 18.5C8 22.6 11.6 26 16 26C20.4 26 24 22.6 24 18.5C24 11 16 7 16 7Z"
              fill="white"
            />
            <line
              x1="16"
              y1="26"
              x2="16"
              y2="17"
              stroke="#277c80"
              strokeWidth="1.8"
              strokeLinecap="round"
            />
            <line
              x1="16"
              y1="21"
              x2="12"
              y2="17.5"
              stroke="#277c80"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
            <line
              x1="16"
              y1="19"
              x2="20"
              y2="15.5"
              stroke="#277c80"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
          <span className="app-header-name">Valesco</span>
        </div>

        <div className="app-header-right">
          <button
            type="button"
            className="dark-toggle"
            onClick={onToggleDark}
            aria-label={darkMode ? t.header.toLight : t.header.toDark}
            title={darkMode ? t.header.toLight : t.header.toDark}
          >
            <svg
              className="dark-toggle-icon"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="4" fill="currentColor" />
              <path
                d="M12 2v2M12 20v2M2 12h2M20 12h2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
            <span className="toggle-switch" aria-hidden="true">
              <span className={`toggle-track${darkMode ? " on" : ""}`} />
            </span>
            <svg
              className="dark-toggle-icon"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"
                fill="currentColor"
              />
            </svg>
          </button>

          <div className="user-menu" ref={menuRef}>
            <button
              className="user-menu-btn"
              type="button"
              onClick={() => setMenuOpen((o) => !o)}
              aria-expanded={menuOpen}
              aria-haspopup="true"
            >
              <span className="user-avatar">
                {username.charAt(0).toUpperCase()}
              </span>
              <span className="user-menu-name">{username}</span>
              <svg
                className={`user-menu-chevron${menuOpen ? " open" : ""}`}
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M2 4L6 8L10 4"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            {menuOpen && (
              <div className="user-menu-dropdown" role="menu">
                {/* Settings */}
                <button
                  className="user-menu-item"
                  type="button"
                  role="menuitem"
                  onClick={openSettings}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    aria-hidden="true"
                  >
                    <circle
                      cx="8"
                      cy="8"
                      r="2"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    />
                    <path
                      d="M8 1v1.5M8 13.5V15M1 8h1.5M13.5 8H15M3.05 3.05l1.06 1.06M11.89 11.89l1.06 1.06M3.05 12.95l1.06-1.06M11.89 4.11l1.06-1.06"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>
                  {t.header.settings}
                </button>

                <div className="user-menu-divider" />

                {/* Logout */}
                <button
                  className="user-menu-item user-menu-item--danger"
                  type="button"
                  role="menuitem"
                  onClick={handleLogout}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M6 2H3C2.4 2 2 2.4 2 3V13C2 13.6 2.4 14 3 14H6"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                    <path
                      d="M11 5L14 8L11 11"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <line
                      x1="14"
                      y1="8"
                      x2="6"
                      y2="8"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>
                  {t.header.logout}
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Settings modal */}
      {settingsOpen && (
        <div
          className="settings-overlay"
          onClick={() => setSettingsOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-label={t.header.settingsTitle}
        >
          <div className="settings-modal" onClick={(e) => e.stopPropagation()}>
            <div className="settings-modal-header">
              <h2 className="settings-modal-title">{t.header.settingsTitle}</h2>
              <button
                className="settings-modal-close"
                type="button"
                onClick={() => setSettingsOpen(false)}
                aria-label="Close"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M3 3L13 13M13 3L3 13"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>

            <div className="settings-body">
              {/* Language */}
              <section className="settings-section">
                <p className="settings-section-label">
                  {t.header.settingsLanguage}
                </p>
                <div className="settings-lang-options">
                  {(["en", "de"] as Language[]).map((lang) => (
                    <button
                      key={lang}
                      type="button"
                      className={`settings-lang-btn${language === lang ? " active" : ""}`}
                      onClick={() => setLanguage(lang)}
                    >
                      {lang === "en" ? "English" : "Deutsch"}
                    </button>
                  ))}
                </div>
              </section>

              {/* Data export */}
              <section className="settings-section">
                <p className="settings-section-label">Data</p>
                <button
                  type="button"
                  className="settings-export-btn"
                  onClick={handleExportCsv}
                  disabled={exporting}
                >
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 16 16"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M8 2v8M5 7l3 3 3-3"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M2 12h12"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                    />
                  </svg>
                  {exporting ? "…" : t.header.exportCsv}
                </button>
              </section>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
